// Helpers compartidos por los conectores OGC (WMS/WFS): fetch con timeout, parseo de
// GetCapabilities y extracción de bbox/formatos. Sin estado, sin dependencias de Vuex.

export async function fetchWithTimeout(url, timeoutMs = 10000, externalSignal) {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort()
    else externalSignal.addEventListener('abort', () => controller.abort(), { once: true })
  }
  try {
    return await fetch(url, { signal: controller.signal })
  } finally {
    clearTimeout(timeoutId)
  }
}

export function parseXmlOrThrow(text) {
  const xml = new DOMParser().parseFromString(text, 'text/xml')
  if (xml.querySelector('parsererror')) {
    throw new Error('El servidor no devolvió un documento XML válido.')
  }
  return xml
}

/**
 * Detecta reportes de error OGC en la respuesta de GetCapabilities:
 * ServiceExceptionReport (WMS) o ExceptionReport (WFS/OWS).
 */
export function checkServiceException(xml) {
  const rootTag = xml.documentElement?.tagName || ''
  if (rootTag === 'ServiceExceptionReport') {
    const msg = xml.querySelector('ServiceException')?.textContent?.trim() || 'El servidor devolvió un error.'
    throw new Error(`Error del servidor: ${msg}`)
  }
  if (rootTag.includes('ExceptionReport')) {
    const msg = xml.querySelector('Exception, ExceptionText')?.textContent?.trim() || 'El servidor devolvió un error.'
    throw new Error(`Error del servidor: ${msg}`)
  }
}

/**
 * Extrae el bounding box (EPSG:4326) de un <Layer> (WMS) o <FeatureType> (WFS)
 * dentro de GetCapabilities. Devuelve null si el servidor no lo informó o el
 * documento no trae los elementos esperados, para no romper "Zoom a capa" después.
 */
export function extractBBox(parentEl, isWfs) {
  try {
    if (!isWfs) {
      const bboxEl = parentEl.querySelector(':scope > LatLonBoundingBox')
      if (!bboxEl) return null
      const bbox = [
        parseFloat(bboxEl.getAttribute('minx')),
        parseFloat(bboxEl.getAttribute('miny')),
        parseFloat(bboxEl.getAttribute('maxx')),
        parseFloat(bboxEl.getAttribute('maxy'))
      ]
      return bbox.every(Number.isFinite) ? bbox : null
    }

    const bboxEl = parentEl.querySelector(':scope > ows\\:WGS84BoundingBox, :scope > WGS84BoundingBox')
    if (!bboxEl) return null
    const lower = bboxEl.querySelector('ows\\:LowerCorner, LowerCorner')?.textContent?.trim().split(/\s+/).map(Number)
    const upper = bboxEl.querySelector('ows\\:UpperCorner, UpperCorner')?.textContent?.trim().split(/\s+/).map(Number)
    if (!lower || !upper || lower.length < 2 || upper.length < 2) return null
    const bbox = [lower[0], lower[1], upper[0], upper[1]]
    return bbox.every(Number.isFinite) ? bbox : null
  } catch (e) {
    return null
  }
}

/**
 * Extrae el mejor INFO_FORMAT tipo JSON que el servicio WMS anuncia soportar en su
 * GetCapabilities (<Capability><Request><GetFeatureInfo><Format>...). No todos los
 * servidores usan "application/json" — ej. ArcGIS Server WMS solo ofrece
 * "application/geo+json". Devuelve null si el servidor no anuncia ningún formato JSON
 * (la consulta de atributos por clic no será posible para esa capa).
 */
export function extractInfoFormat(xml) {
  const formats = [...xml.querySelectorAll('GetFeatureInfo > Format')]
    .map(f => f.textContent.trim().toLowerCase())
  if (formats.includes('application/json')) return 'application/json'
  if (formats.includes('application/geo+json')) return 'application/geo+json'
  return null
}
